import React, { useEffect, useRef } from 'react';
import * as Highcharts from 'highcharts';
import HighchartsPareto from 'highcharts/modules/pareto';
import { t } from 'i18next';

// Initialize the pareto module
HighchartsPareto(Highcharts);

interface ParetoDataItem {
    name: string;
    value: number;
    color?: string;
}

interface LivisParetoHighchartsProps {
    data: ParetoDataItem[];
    height?: number;
    showTooltip?: boolean;
    showLabels?: boolean;
    colors?: {
        column: string;
        pareto: string;
    };
    onPointClick?: (point: any) => void;
    columnName?: string;
    paretoName?: string;
    yAxisTitles?: {
        left?: string;
        right?: string;
    };
    // NEW: Optional x-axis title
    x_title?: string;
    // NEW: Optional y-axis max caps
    yAxisRightMax?: number; // for percentage axis (defaults to 100)
    yAxisLeftMax?: number;  // for frequency axis
    noDataMessage?: string; // NEW: Custom no data message
}

const LivisParetoHighcharts: React.FC<LivisParetoHighchartsProps> = ({
    data = [],
    height = 400,
    showTooltip = true,
    showLabels = false,
    colors = {
        column: '#B50000',
        pareto: '#8B4513'
    },
    onPointClick,
    columnName = 'Defect Frequency',
    paretoName = 'Cumulative Percentage',
    yAxisTitles = {
        left: 'DEFECT FREQUENCY (%)',
        right: 'CUMULATIVE PERCENTAGE (%)'
    },
    x_title,
    yAxisRightMax = 100,
    yAxisLeftMax,
    noDataMessage = "No Record Found" // NEW: Default message
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<Highcharts.Chart | null>(null);

    // NEW: Check for empty data and render no data message
    const hasData = data && data.length > 0;

    // --- No Data Template ---
        const NoDataTemplate = () => (
            <div
                className="livis-dashboard-no-record-centered"
                style={{
                    width: '100%',
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <p>{t(noDataMessage)}</p>
            </div>
        );

    

    useEffect(() => {
        if (!chartRef.current || !data.length) return;

        const chartOptions: Highcharts.Options = {
            chart: {
                type: 'column',
                height: height,
                backgroundColor: 'transparent',
                spacing: [20, 20, 20, 20]
            },
            title: {
                text: undefined
            },
            subtitle: {
                text: undefined
            },
            xAxis: {
                categories: data.map(item => item.name),
                crosshair: true,
                labels: {
                    style: {
                        color: '#1F2937',
                        fontSize: '12px',
                        fontWeight: '400'
                    }
                },
                lineWidth: 0,
                tickWidth: 0,
                title: x_title ? { text: x_title } : undefined
            },
            yAxis: [
                {
                    title: {
                        text: yAxisTitles.left,
                        style: {
                            color: colors.column,
                            fontSize: '12px',
                            fontWeight: '600'
                        }
                    },
                    labels: {
                        style: {
                            color: colors.column,
                            fontSize: '11px'
                        }
                    },
                    gridLineColor: '#E5E7EB',
                    gridLineWidth: 1,
                    max: typeof yAxisLeftMax === 'number' ? yAxisLeftMax : undefined,
                    ceiling: typeof yAxisLeftMax === 'number' ? yAxisLeftMax : undefined,
                    floor: 0,
                    endOnTick: false
                },
                {
                    title: {
                        text: yAxisTitles.right,
                        style: {
                            color: colors.pareto,
                            fontSize: '12px',
                            fontWeight: '600'
                        }
                    },
                    labels: {
                        format: '{value}%',
                        style: {
                            color: colors.pareto,
                            fontSize: '11px'
                        }
                    },
                    minPadding: 0,
                    maxPadding: 0,
                    max: typeof yAxisRightMax === 'number' ? yAxisRightMax : 100,
                    min: 0,
                    opposite: true,
                    gridLineWidth: 0,
                    endOnTick: false,
                    ceiling: typeof yAxisRightMax === 'number' ? yAxisRightMax : 100,
                    floor: 0
                }
            ],
            legend: {
                enabled: false
            },
            tooltip: {
                enabled: showTooltip,
                shared: true,
                backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
                borderColor: 'transparent',
                borderRadius: 8,
                style: {
                    color: 'var(--livis-white-color)',
                },
                formatter: function() {
                    const points = this.points || [this.point];
                    let tooltip = `<div style="padding: 8px;">`;
                    
                    points.forEach((point: any) => {
                        if (point.series.name === paretoName) {
                            const capped = Math.min(point.y ?? 0, (typeof yAxisRightMax === 'number' ? yAxisRightMax : 100));
                            tooltip += `
                                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                                    <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colors.pareto}; border-radius: 50%; margin-right: 8px;"></span>
                                    <span style="color: ${colors.pareto};">${paretoName}:</span>
                                    <span style="font-weight: bold; margin-left: 8px;">${capped.toFixed(2)}%</span>
                                </div>
                            `;
                        } else {
                            tooltip += `
                                <div style="display: flex; align-items: center; margin-bottom: 4px;">
                                    <span style="display: inline-block; width: 10px; height: 10px; background-color: ${colors.column}; border-radius: 50%; margin-right: 8px;"></span>
                                    <span style="color: ${colors.column};">${columnName}:</span>
                                    <span style="font-weight: bold; margin-left: 8px;">${point.y?.toLocaleString()}</span>
                                </div>
                            `;
                        }
                    });
                    
                    tooltip += `</div>`;
                    return tooltip;
                },
                useHTML: true
            },
            plotOptions: {
                column: {
                    borderRadius: 6,
                    borderWidth: 0,
                    dataLabels: {
                        enabled: showLabels,
                        style: {
                            color: '#FFFFFF',
                            fontSize: '11px',
                            fontWeight: '600',
                            textOutline: 'none'
                        }
                    },
                    point: {
                        events: {
                            click: function() {
                                if (onPointClick) {
                                    const categoryIndex = this.x;
                                    const clickedItem = data[categoryIndex || 0];
                                    onPointClick(clickedItem);
                                }
                            }
                        }
                    }
                },
                pareto: {
                    color: colors.pareto,
                    lineWidth: 3,
                    marker: {
                        enabled: true,
                        radius: 6,
                        fillColor: colors.pareto,
                        lineWidth: 2,
                        lineColor: '#FFFFFF'
                    },
                    dataLabels: {
                        enabled: false
                    },
                    tooltip: {
                        valueDecimals: 2,
                        valueSuffix: '%'
                    }
                }
            },
            series: [
                {
                    type: 'pareto',
                    name: paretoName,
                    yAxis: 1,
                    zIndex: 10,
                    baseSeries: 1,
                    tooltip: {
                        valueDecimals: 2,
                        valueSuffix: '%'
                    }
                },
                {
                    name: columnName,
                    type: 'column',
                    zIndex: 2,
                    data: data.map(item => ({
                        name: item.name,
                        y: item.value,
                        color: item.color || colors.column
                    })),
                    color: colors.column
                }
            ],
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            }
        };

        // Destroy existing chart
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create new chart
        chartInstance.current = Highcharts.chart(chartRef.current, chartOptions);

        // Cleanup function
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [data, height, showTooltip, showLabels, colors, onPointClick, columnName, paretoName, yAxisTitles]);

    if (!hasData) {
        return <NoDataTemplate />;
    }

    return <div ref={chartRef} style={{ width: '100%', height: `${height}px` }} />;
};

export default LivisParetoHighcharts;